import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useWorkspaceStore, type Workspace } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'

/**
 * useWorkspace — loads the user's workspaces and keeps the Zustand store in sync.
 *
 * Behavior:
 * 1. Fires GET /api/v1/workspaces via TanStack Query on mount (cached for 60 s).
 * 2. On success, calls `store.setWorkspaces()` which:
 *    - Populates the workspace list.
 *    - Auto-selects the first workspace if no currentWorkspaceId is persisted.
 *    - Re-hydrates currentWorkspace from the fresh API data.
 * 3. Returns the reactive store values so components re-render on workspace switch.
 *
 * @returns `{ currentWorkspace, workspaces, switchWorkspace, isLoading }`
 */
export function useWorkspace() {
  const { currentWorkspace, workspaces, switchWorkspace, setWorkspaces } = useWorkspaceStore()

  const { data, isLoading } = useQuery<ApiResponse<Workspace[]>>({
    queryKey: ['workspaces'],
    queryFn: () => api.get<ApiResponse<Workspace[]>>('/v1/workspaces'),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
      setWorkspaces(data.data)
    }
  }, [data, setWorkspaces])

  return {
    currentWorkspace,
    workspaces,
    switchWorkspace,
    isLoading,
  }
}
